import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { CloudinaryResourceDetail } from './CloudinaryResourceDetail';
import { CloudinaryResourceList } from './CloudinaryResourceList';
import type { CloudinaryResource } from './CloudinaryResourceList';

interface CloudinaryBrowserProps {
  className?: string;
}

type ResourceType = 'image' | 'video' | 'raw';

export function CloudinaryBrowser({ className = '' }: CloudinaryBrowserProps) {
  const [resourceType, setResourceType] = useState<ResourceType>('image');
  const [prefix, setPrefix] = useState<string>('');
  const [selectedResource, setSelectedResource] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  // Query for Cloudinary resources
  const { 
    data: resourcesData, 
    isPending: resourcesLoading, 
    error: resourcesError,
    refetch: refetchResources 
  } = trpc.media.listCloudinaryResources.useQuery({
    resourceType,
    prefix: prefix || undefined,
    maxResults: 50,
  });

  // Query for detailed resource info when one is selected
  const { 
    data: resourceDetail, 
    isPending: detailLoading 
  } = trpc.media.getCloudinaryResource.useQuery(
    { publicId: selectedResource as string },
    { enabled: !!selectedResource }
  );

  // Utils for invalidating queries
  const utils = trpc.useContext();

  // Delete mutation
  const deleteResource = trpc.media.deleteCloudinaryResource.useMutation({
    onSuccess: () => {
      // Invalidate and refetch resources list
      utils.media.listCloudinaryResources.invalidate();
      // Clear selected resource
      setSelectedResource(null);
      // Close modal
      setShowDeleteModal(false);
    },
    onError: (error: { message: string }) => {
      alert(`Failed to delete resource: ${error.message}`);
    },
  });

  const handleResourceTypeChange = (type: ResourceType) => {
    setResourceType(type);
    setSelectedResource(null);
  };

  const handlePrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrefix(e.target.value);
    setSelectedResource(null);
  };

  // Cast resources to proper type
  const resourcesList = (resourcesData?.resources ?? []) as unknown as CloudinaryResource[];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header and Controls */}
      <div className="bg-gray-50 rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Cloudinary Browser</h2>
            <p className="text-gray-600 mt-1">
              Browse all media stored in Cloudinary and view database associations
            </p>
          </div>
          <button
            onClick={() => refetchResources()}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Resource Type
            </label>
            <select
              value={resourceType}
              onChange={(e) => handleResourceTypeChange(e.target.value as ResourceType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-info-500 focus:border-info-500"
            >
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="raw">Raw Files</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Folder Prefix (optional)
            </label>
            <input
              type="text"
              value={prefix}
              onChange={handlePrefixChange}
              placeholder="e.g., wineryxplorer/venue/"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-info-500 focus:border-info-500"
            />
          </div>
        </div>
      </div>

      {/* Error State */}
      {resourcesError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-red-400">⚠️</div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading resources</h3>
              <p className="text-sm text-red-700 mt-1">{resourcesError.message}</p>
              {resourcesError.message.includes('Cloudinary configuration missing') && (
                <div className="mt-2 text-sm text-red-600">
                  <p>To use the Cloudinary Browser:</p>
                  <ol className="list-decimal list-inside mt-1 space-y-1">
                    <li>Sign up at <a href="https://cloudinary.com" target="_blank" rel="noopener noreferrer" className="underline">cloudinary.com</a></li>
                    <li>Get your credentials from the Cloudinary dashboard</li>
                    <li>Add them to your <code className="bg-red-100 px-1 rounded">apps/api/.env</code> file</li>
                    <li>Restart the API server</li>
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Configuration Missing State */}
      {resourcesData && 'error' in resourcesData && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-yellow-400">⚠️</div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Cloudinary Configuration Required</h3>
              <p className="text-sm text-yellow-700 mt-1">{(resourcesData as { error: string }).error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Resources List */}
        <CloudinaryResourceList
          resources={resourcesList}
          isLoading={resourcesLoading}
          selectedResource={selectedResource}
          onSelectResource={setSelectedResource}
        />

        {/* Resource Detail */}
        <CloudinaryResourceDetail
          selectedResource={selectedResource}
          resourceDetail={resourceDetail}
          isLoading={detailLoading}
          onRequestDelete={() => setShowDeleteModal(true)}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          if (selectedResource) {
            deleteResource.mutate({ publicId: selectedResource });
          }
        }}
        title="Delete Cloudinary Resource"
        message={`Are you sure you want to permanently delete this resource? This action cannot be undone and will remove the media from Cloudinary and all database references.`}
        confirmText="Delete Permanently"
        cancelText="Cancel"
        isDestructive={true}
        isLoading={deleteResource.isPending}
      />
    </div>
  );
}